import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isLocalAudio, type SentenceAudioBlock } from "@/utils/audioBlockMap";
import { audioPreparationItems, planAudioPreparation } from "@/utils/audioPreparation";
import { canonicalJson } from "@/utils/cachedPlayback";
import { sentenceAudioStates, type AudioArtifactStatus } from "@/utils/audioReadiness";
import { audioSourceBlockIds } from '@/utils/cachedAudioBlocks';

export const AUDIO_PLAN_DEBOUNCE_MS = 900;
export const AUDIO_STATUS_POLL_MS = 5000;
type Job = { id: string; revision: string; total: number; completed: number; failed: number;
  pending: number; paused: boolean; last_work_at: string | null; items?: AudioArtifactStatus[];
  cancelled?: boolean;
  retry_at?: string | null; recovery_reason?: 'provider_limit' | 'retry_tomorrow' | 'budget' | 'attention' | null;
  bindings?: { input_index: number; input_hash: string }[] };
type Snapshot = { signature: string; job: Job | null; error: string; hashes?: ReadonlyMap<string, string>; checkedAt?: number };

export async function audioPreparationRequest(body: Record<string, unknown>): Promise<Job | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) throw new Error("Sign in to update audio preparation.");
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/audio-preparation`, {
    method: "POST", headers: { Authorization: `Bearer ${data.session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY, "Content-Type": "application/json" },
    body: JSON.stringify(body), signal: AbortSignal.timeout(25000),
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error || "Unable to update audio preparation.");
  return payload.job ? { ...payload.job, bindings: payload.bindings } : null;
}

/** Submit exact current inputs once; the pod continues independently of React,
 * browser visibility, navigation, logout, and the browser's polling lifecycle. */
export function useServerAudioPreparation({ screenplayId, sentences, revisions, activeBlockId, paused, documentRef, castingState,enabled=true,expectedSpeechLanguage }: {
  screenplayId: string; sentences: SentenceAudioBlock[]; revisions: ReadonlyMap<string, string>;
  activeBlockId: string; paused: boolean;
  documentRef?: RefObject<HTMLDivElement | null>;
  castingState?: unknown;
  enabled?:boolean;
  expectedSpeechLanguage?: string;
}) {
  const [visibleBlocks, setVisibleBlocks] = useState<Set<string>>(new Set());
  const [snapshot, setSnapshot] = useState<Snapshot>({ signature: "", job: null, error: "" });
  const [reconnect, setReconnect] = useState(0);
  const items = useMemo(() => audioPreparationItems(sentences, revisions), [sentences, revisions]);
  const spokenTotal = sentences.filter(s => !isLocalAudio(s)).length;
  const localTotal = sentences.length - spokenTotal;
  const signature = canonicalJson({ screenplayId, castingState, expectedSpeechLanguage, total: spokenTotal, keys: items.map(i => i.key) });
  const serial = useRef<Promise<void>>(Promise.resolve());
  const current = useRef({ signature, paused, activeBlockId, items });
  const pollRef = useRef<(() => Promise<void>) | null>(null);
  const submitRef = useRef<((flush?: boolean) => void) | null>(null);
  // Use the latest committed plan. Queue synchronously: navigation can hide the
  // Activity immediately after blur. Repeated unchanged exits are a no-op.
  const flush = useCallback(() => { submitRef.current?.(true); }, []);
  useEffect(() => { current.current = { signature, paused, activeBlockId, items }; });
  // Completion is confirmed by the regular status poll, not a round trip for
  // every prefetched sentence. Never optimistically acknowledge stale audio.
  const markReady = useCallback((_key: string) => {}, []);
  const blockIds = [...new Set(sentences.flatMap(audioSourceBlockIds))].join("\n");
  useEffect(() => {
    const region = documentRef?.current;
    if (!region || typeof IntersectionObserver === "undefined") return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const id = (entry.target as HTMLElement).dataset.audioBlock;
        if (id) { if (entry.isIntersecting) visible.add(id); else visible.delete(id); }
      }
      setVisibleBlocks(new Set(visible));
    });
    region.querySelectorAll("[data-audio-block]").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [blockIds, documentRef]);
  const priorities = canonicalJson(planAudioPreparation(items, visibleBlocks, activeBlockId, new Set())
    .slice(0, 12).map(item => ({ voice_id: item.sentence.voiceId, text: item.sentence.text,
      synthesis_options: item.sentence.synthesisOptions })));
  const acceptedJob = snapshot.signature === signature ? snapshot.job?.id : undefined;
  useEffect(() => {
    if (!documentRef || !acceptedJob) return;
    const timer = setTimeout(() => {
      void audioPreparationRequest({ action: "prioritize", screenplay_id: screenplayId,
        items: JSON.parse(priorities) }).catch(() => {}); // Priority is advisory; durable work continues.
    }, 500);
    return () => clearTimeout(timer);
  }, [acceptedJob, priorities, screenplayId, documentRef]);

  useEffect(() => {
    if(!enabled)return;
    let disposed = false, polling = false, timer: ReturnType<typeof setTimeout> | undefined;
    let expectedRevision: string | undefined;
    let hashes = new Map<string, string>();
    const update = (job: Job | null, error = "") => {
      if (!disposed && current.current.signature === signature) setSnapshot({ signature, job, error, hashes, checkedAt: Date.now() });
    };
    const poll = async () => {
      if (disposed || polling || !expectedRevision) return;
      polling = true;
      try {
        const job = await audioPreparationRequest({ action: "status", screenplay_id: screenplayId, foreground: current.current.paused });
        if (job?.revision === expectedRevision) update(job);
        else update(null, "The server audio plan changed in another editor. Save and reopen this screenplay to sync it safely.");
      } catch (e) {
        if (!disposed) setSnapshot(previous => ({ ...previous, signature,
          error: e instanceof Error ? e.message : "Connection interrupted. The server keeps preparing audio." }));
      } finally { polling = false; }
    };
    const loop = async () => {
      await poll();
      if (!disposed) timer = setTimeout(loop, AUDIO_STATUS_POLL_MS);
    };
    let submitted = false;
    const submit = (flush = false) => {
      if (submitted || disposed) return;
      submitted = true;
      clearTimeout(debounce);
      // Serialize submissions across edits. An older request cannot arrive after
      // its replacement and overwrite the new queue. Cleanup never cancels jobs.
      serial.current = serial.current.catch(() => {}).then(async () => {
        if ((disposed && !flush) || current.current.signature !== signature || !screenplayId ||
          (spokenTotal > 0 && items.length === 0)) return;
        try {
          const counts = new Map<string, number>();
          items.forEach(item => counts.set(item.key, (counts.get(item.key) ?? 0) + 1));
          const planned = planAudioPreparation(items, new Set(), current.current.activeBlockId, new Set());
          const job = await audioPreparationRequest({ action: "submit", screenplay_id: screenplayId,
            expected_casting_state: castingState,
            ...(expectedSpeechLanguage !== undefined ? { expected_speech_language: expectedSpeechLanguage === 'auto' ? null : expectedSpeechLanguage } : {}),
            total: spokenTotal, foreground: current.current.paused,
            items: planned.map(item => ({ voice_id: item.sentence.voiceId,
              voice_revision: revisions.get(item.sentence.voiceId), text: item.sentence.text,
              synthesis_options: item.sentence.synthesisOptions, occurrences: counts.get(item.key),
              ...(item.sentence.cacheSources && item.sentence.cacheSources.length > 1 ? { cache_sources: item.sentence.cacheSources.map(s => ({ text:s.text,synthesis_options:s.synthesisOptions })) } : {}) })),
          });
          if (disposed) return;
          hashes = new Map((job?.bindings ?? []).flatMap(binding => {
            const key = planned[binding.input_index]?.key;
            return key ? [[key, binding.input_hash] as const] : [];
          }));
          expectedRevision = job?.revision;
          update(job);
          pollRef.current = poll;
          timer = setTimeout(loop, AUDIO_STATUS_POLL_MS);
        } catch (e) {
          update(null, e instanceof Error ? e.message : "Unable to sync audio preparation. Retry shortly.");
          if (!disposed) timer = setTimeout(() => setReconnect(n => n + 1), 30000);
        }
      });
    };
    const debounce = setTimeout(() => submit(), AUDIO_PLAN_DEBOUNCE_MS);
    submitRef.current = submit;
    return () => {
      disposed = true; clearTimeout(debounce); clearTimeout(timer); pollRef.current = null;
      if (submitRef.current === submit) submitRef.current = null;
    };
    // The canonical signature includes all audio inputs. Scrolling/foreground
    // changes must not resubmit thousands of unchanged sentences.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, reconnect,enabled]);
  useEffect(() => { void pollRef.current?.(); }, [paused]);

  const total = sentences.length;
  const matched = snapshot.signature === signature;
  // Zero is a valid confirmed result, not a substitute for a missing snapshot.
  // Match the exact current inputs so edits never inherit an old plan's percent.
  const progressKnown = matched && snapshot.job !== null;
  const completed = localTotal + (matched && !snapshot.job?.cancelled ? Math.min(spokenTotal, snapshot.job?.completed ?? 0) : 0);
  const errors = matched ? (snapshot.job?.failed ?? 0) + (snapshot.error ? 1 : 0) : 0;
  const percent = total ? Math.floor(completed * 100 / total) : 0;
  const missingVoices = spokenTotal - items.length;
  const unit = localTotal ? "audio parts (including local pauses/unspoken text)"
    : sentences.some(s => s.id.startsWith('baseline:')) ? 'audio clips (including neutral versions)' : "audio blocks";
  const recovery = matched && snapshot.job ? audioRecoveryNotice(snapshot.job) : "";
  // Connection checks and in-progress recovery are admin diagnostics, not
  // user-facing failures. Only a terminal recovery result requires attention.
  const notice = matched && snapshot.error?.includes('changed in another editor') ? snapshot.error
    : completed < total && snapshot.job?.recovery_reason === 'attention' ? recovery : "";
  const status = matched && snapshot.job?.cancelled ? 'Background audio conversion was cancelled. Saved audio remains available. Confirm conversion on the Voices page to restart it.'
    : !total ? "Waiting for screenplay text and voices."
    : matched && snapshot.error ? (snapshot.error.includes('changed in another editor') ? snapshot.error : `Preparing audio. ${progressKnown ? `Last confirmed audio: ${completed}/${total} ${unit}.` : 'Audio preparation continues on the server.'}`)
    : !matched || !snapshot.job ? "Syncing screenplay audio with the server… Saved audio will be reused."
    : completed === total ? `All screenplay audio is prepared: ${completed}/${total} ${unit}. Playback may still need to download and buffer audio.`
    : snapshot.job.recovery_reason === 'attention' ? `Audio needs help: ${completed}/${total} ${unit} ready. ${notice}`
    : `Preparing audio on the server: ${completed}/${total} ${unit}. ${notice ? `${notice} ` : ""}${missingVoices ? `${missingVoices} need an available voice. ` : ""}${paused ? "Playback has priority. " : ""}Preparation continues after closing this page.`;
  // Completion counters keep the last exact-input verified snapshot during a
  // polling outage. Playback/recovery lookups still become unknown: a stale
  // progress display must never authorize a generation, repair or paid retry.
  const progressStale=Boolean(matched&&snapshot.error);
  const confirmedAudioStates=useMemo(()=>sentenceAudioStates(sentences,revisions,
    matched?snapshot.hashes:undefined,matched?snapshot.job?.items:undefined),[sentences,revisions,matched,snapshot]);
  const audioStates = useMemo(() => progressStale?sentenceAudioStates(sentences, revisions,
    matched ? snapshot.hashes : undefined, matched ? snapshot.job?.items : undefined,true):confirmedAudioStates,
    [sentences,revisions,matched,snapshot,progressStale,confirmedAudioStates]);
  return { total, completed, percent, progressKnown, errors, status, notice, markReady, flush, audioStates,
    needsAttention: Boolean(matched && snapshot.job?.recovery_reason === 'attention'),
    cancelled:Boolean(matched&&snapshot.job?.cancelled),
    confirmedAudioStates,progressStale,checkedAt: matched ? snapshot.checkedAt : undefined };
}

export function audioRecoveryNotice(job: Pick<Job, 'retry_at' | 'recovery_reason'>, now = Date.now()): string {
  if (!job.recovery_reason) return "";
  if (job.recovery_reason === 'attention') return "Some audio couldn’t be completed. Your script and finished recordings are saved. Open Audio recovery to review affected sentences. Please contact support for help if needed.";
  const at = job.retry_at ? Date.parse(job.retry_at) : NaN;
  const retry = Number.isFinite(at) && at > now
    ? `Automatic retry at ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' }).format(at)}.`
    : 'Retries are automatic.';
  const reason = job.recovery_reason === 'budget' ? 'Waiting for the daily audio budget to reset.'
    : job.recovery_reason === 'retry_tomorrow' ? 'Some audio is waiting for the next Gemini quota window.'
    : 'Waiting for Gemini to accept more audio requests.';
  return `${reason} ${retry}`;
}
